import {
    array,
    boolean,
    enum as zodEnum,
    intersection,
    nativeEnum,
    number,
    object,
    record,
    string,
    TypeOf,
} from 'zod';

import { Alignment } from './common';

/* ------------------------------ RenewalPeriod ----------------------------- */

export const RenewalPeriod = zodEnum(['daily', 'weekly', 'monthly', 'yearly']);
export type RenewalPeriod = TypeOf<typeof RenewalPeriod>;

/* ----------------------------- PriceBandTypes ----------------------------- */

export const PriceBandTypes = object({
    daily: string().optional(),
    weekly: string().optional(),
    monthly: string().optional(),
    yearly: string().optional(),
});
export type PriceBandTypes = TypeOf<typeof PriceBandTypes>;

/* -------------------------------- PriceBand ------------------------------- */

export const PriceBand = object({
    id: string(),
    name: string(),
    value: number(),
    currency: string(),
    interval: RenewalPeriod,
});
export type PriceBand = TypeOf<typeof PriceBand>;

/* ---------------------------- SubscriptionImage --------------------------- */

export const SubscriptionImage = object({
    master: string(),
    daily: string().optional(),
    weekly: string().optional(),
    monthly: string().optional(),
    yearly: string().optional(),
});
export type SubscriptionImage = TypeOf<typeof SubscriptionImage>;

/* ---------------------------- SubscriptionTile ---------------------------- */

export const SubscriptionTile = object({
    name: object({
        alignment: nativeEnum(Alignment),
        visible: boolean(),
    }),
    description: object({
        alignment: nativeEnum(Alignment),
        visible: boolean(),
    }),
    image: object({
        visible: boolean(),
    }),
});
export type SubscriptionTile = TypeOf<typeof SubscriptionTile>;

/* ------------------------- SubscriptionPageDisplay ------------------------ */

export const SubscriptionPageDisplay = object({
    display: object({
        tile: SubscriptionTile,
        name: object({
            alignment: nativeEnum(Alignment),
            visible: boolean(),
        }),
        description: object({
            alignment: nativeEnum(Alignment),
            visible: boolean(),
        }),
        image: object({
            visible: boolean(),
        }),
    }),
});
export type SubscriptionPageDisplay = TypeOf<typeof SubscriptionPageDisplay>;

/* ------------------------------ GroupDisplay ------------------------------ */

export const GroupDisplay = object({
    display: object({
        tile: SubscriptionTile.merge(
            object({
                details: object({
                    alignment: nativeEnum(Alignment),
                    visible: boolean(),
                }),
            }),
        ),
    }),
});
export type GroupDisplay = TypeOf<typeof GroupDisplay>;

/* ------------------- SubscriptionImageAvailabilityWindow ------------------ */

export const SubscriptionImageAvailabilityWindow = object({
    id: string(),
    priceBands: PriceBandTypes,
});
export type SubscriptionImageAvailabilityWindow = TypeOf<
    typeof SubscriptionImageAvailabilityWindow
>;

/* ---------------------------------- Tier ---------------------------------- */

export const Tier = object({
    id: string(),
    name: string(),
    description: string().optional(),
    details: string().optional(),
    image: SubscriptionImage.optional(),
    exclusive: boolean(),
    availabilityWindows: SubscriptionImageAvailabilityWindow,
    type: string().default('tier'),
});
export type Tier = TypeOf<typeof Tier>;

/* --------------------------------- Bundle --------------------------------- */

export const Bundle = object({
    id: string(),
    name: string(),
    description: string().optional(),
    details: string().optional(),
    image: SubscriptionImage.optional(),
    tierIds: array(string()),
    availabilityWindows: SubscriptionImageAvailabilityWindow,
    type: string().default('bundle'),
});
export type Bundle = TypeOf<typeof Bundle>;

/* ---------------------------------- Group --------------------------------- */

export const Group = object({
    id: string(),
    name: string(),
    description: string().optional(),
    image: SubscriptionImage.optional(),
    children: array(string()),
}).merge(
    object({
        display: object({
            tile: SubscriptionTile.merge(
                object({
                    details: object({
                        alignment: nativeEnum(Alignment),
                        visible: boolean(),
                    }),
                }),
            ).deepPartial(),
        }).optional(),
    }),
);
export type Group = TypeOf<typeof Group>;

/* ----------------------- SubscriptionBackgroundImage ---------------------- */

export const SubscriptionBackgroundImage = intersection(
    object({
        master: string(),
    }),
    record(string(), string().optional()),
);
export type SubscriptionBackgroundImage = TypeOf<
    typeof SubscriptionBackgroundImage
>;

/* ---------------------------- SubscriptionPage ---------------------------- */

export const SubscriptionPage = object({
    id: string(),
    name: string(),
    description: string().optional(),
    image: SubscriptionBackgroundImage.optional(),
    groupIds: array(string()),
}).merge(SubscriptionPageDisplay.deepPartial());
export type SubscriptionPage = TypeOf<typeof SubscriptionPage>;

/* -------------------------- SubscriptionResponse -------------------------- */

export const SubscriptionResponse = object({
    subscriptionPage: SubscriptionPage,
    groups: array(Group),
    bundles: array(Bundle),
    tiers: array(Tier),
    extend: object({
        priceBands: array(PriceBand),
    }).optional(),
});
export type SubscriptionResponse = TypeOf<typeof SubscriptionResponse>;
